import {md2html} from '@github-ui/md2html'
import {publishOptimisticFields} from '@github-ui/optimistic-connection/connectionMutationBridge'
import {commitMutation, graphql} from 'react-relay'
import type {Environment} from 'relay-runtime'

import type {
  updateIssueCommentBodyMutation,
  updateIssueCommentBodyMutation$data,
  updateIssueCommentBodyMutation$rawResponse,
  UpdateIssueCommentInput,
} from './__generated__/updateIssueCommentBodyMutation.graphql'

export function commitUpdateIssueCommentBodyMutation({
  environment,
  input: {id, body, bodyVersion},
  onCompleted,
  onError,
  withOptimisticResponse = false,
}: {
  environment: Environment
  input: {id: string; body: string | null; bodyVersion: string}
  onCompleted?: (response?: updateIssueCommentBodyMutation$data) => void
  onError?: (error: Error) => void
  withOptimisticResponse?: boolean
}) {
  if (!body) {
    return
  }

  const inputHash: UpdateIssueCommentInput = {
    id,
    body,
    bodyVersion,
  }

  const optimisticBodyHTML = md2html(body)

  const optimisticResponse: updateIssueCommentBodyMutation$rawResponse | undefined = withOptimisticResponse
    ? {
        updateIssueComment: {
          issueComment: {
            id,
            body,
            bodyHTML: optimisticBodyHTML,
            bodyVersion,
            viewerCanUpdate: false,
          },
        },
      }
    : undefined

  const optimistic = publishOptimisticFields(
    id,
    withOptimisticResponse ? {body, bodyHTML: optimisticBodyHTML, bodyVersion} : undefined,
  )

  let disposable
  try {
    disposable = commitMutation<updateIssueCommentBodyMutation>(environment, {
      // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
      mutation: graphql`
        mutation updateIssueCommentBodyMutation($input: UpdateIssueCommentInput!) @raw_response_type {
          updateIssueComment(input: $input) {
            issueComment {
              id
              body
              bodyHTML(unfurlReferences: true)
              bodyVersion
              viewerCanUpdate
            }
          }
        }
      `,
      variables: {
        input: inputHash,
      },
      optimisticResponse,
      onCompleted: response => {
        const issueComment = response.updateIssueComment?.issueComment
        if (!issueComment) {
          // The editor clears isSubmitting from onError, so returning silently here left it stuck.
          const error = new Error('Failed to update comment')
          optimistic.failed(error)
          if (onError) onError(error)
          return
        }
        optimistic.completed({
          body: issueComment.body,
          bodyHTML: issueComment.bodyHTML,
          bodyVersion: issueComment.bodyVersion,
        })
        if (onCompleted) onCompleted(response)
      },
      onUnsubscribe: () => optimistic.failed(new Error('Updating the comment was cancelled')),
      onError: error => {
        optimistic.failed(error)
        if (onError) onError(error)
      },
    })
  } catch (error) {
    optimistic.failed(error instanceof Error ? error : new Error('Failed to update comment'))
    throw error
  }

  return disposable
}
